import { useNavigate } from "react-router-dom";
import type { DashboardRecording } from "../types/listOfRecordingsTypes";

interface VideoTableProps {
  recordings: DashboardRecording[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function VideoTable({
  recordings,
  currentPage,
  totalPages,
  onPageChange,
}: VideoTableProps) {
  const navigate = useNavigate();

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  if (recordings.length === 0) {
    return (
      <div className="empty-state">
        No recordings found.
      </div>
    );
  }

  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Patient ID</th>
              <th>Patient Name</th>
              <th>Submitted On</th>
              <th>Reviewed On</th>
              <th>Reviewed By</th>
              <th>AI Score</th>
              <th>Reviewer Score</th>
              <th>Rewards</th>
              <th>Review Status</th>
            </tr>
          </thead>

          <tbody>
            {recordings.map((recording) => (
              <tr
                key={recording.sessionId}
                className="row-clickable"
                onClick={() =>
                  navigate(`/videos/${recording.sessionId}`)
                }
                title="Click to view details"
              >
                <td>
                  <strong>
                    {recording.patientId}
                  </strong>
                </td>

                <td>
                  {recording.patientName}
                </td>

                <td>
                  {new Date(
                    recording.submittedAt
                  ).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </td>

                <td>
                  {recording.reviewedAt
                    ? new Date(
                        recording.reviewedAt
                      ).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"}
                </td>

                <td>
                  {recording.reviewedBy}
                </td>

                <td>
                  {recording.aiScore}
                </td>

                <td>
                  {recording.reviewerScore}
                </td>

                <td>
                  {recording.rewards}
                </td>

                <td>
                  <span
                    className={`dashboard-status-pill ${
                      recording.reviewStatus ===
                      "Accepted"
                        ? "dashboard-status-pill--accepted"
                        : "dashboard-status-pill--review"
                    }`}
                  >
                    {recording.reviewStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
        >
          ⏮
        </button>

        <button
          onClick={() =>
            goToPage(currentPage - 1)
          }
          disabled={currentPage === 1}
        >
          ◀
        </button>

        {[...Array(totalPages)].map(
          (_, index) => {
            const page = index + 1;

            return (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={
                  page === currentPage
                    ? "active"
                    : ""
                }
              >
                {page}
              </button>
            );
          }
        )}

        <button
          onClick={() =>
            goToPage(currentPage + 1)
          }
          disabled={currentPage === totalPages}
        >
          ▶
        </button>

        <button
          onClick={() =>
            goToPage(totalPages)
          }
          disabled={currentPage === totalPages}
        >
          ⏭
        </button>
      </div>
    </>
  );
}

export default VideoTable;